/**
 * SKU Risk — inventory domain operational semantics
 *
 * Derives the risk state of a SKU from live product fields.
 * Risk is a function of stock position relative to operational thresholds.
 *
 * Priority order:
 *   1. stock-out   — zero units; revenue exposure is immediate
 *   2. critical    — at or below safety stock; lead time is operationally relevant
 *   3. low         — at or below reorder point; replenishment action required
 *   4. suppressed  — Amazon listing suppressed or inactive_out_of_stock
 *   5. nominal     — no anomaly detected
 *
 * Source fields: stock / quantity, safety_stock_units, reorder_point_units,
 * lead_time_days, listing_status. Falls back to legacy field names and
 * sensible defaults when fields are absent.
 *
 * Returns a descriptor used by SkuRiskBadge and the supply-chain cascade.
 * The `icon` field is a string name resolved by badge components via ICON_MAP.
 */

/**
 * Returns the available stock for a product, honoring Amazon FBA fulfillable quantity
 * for FBA-managed SKUs while preserving the legacy local-stock semantics for everything else.
 */
export function getAvailableStock(product) {
  if (product?.managedByFBA === true) {
    return product?.fbaInventory?.fulfillableQuantity ?? 0;
  }

  return product?.stock ?? product?.quantity ?? 0;
}

/**
 * @param {object} product — Product document from API
 * @returns {{ level, label, sub, icon, rowBg, rowClass, badgeClass }}
 */
export function computeSkuRisk(product) {
  const stock = getAvailableStock(product);
  const safetyStock = product.safety_stock_units ?? product.reorderPoint ?? 20;
  const reorderPoint = product.reorder_point_units ?? product.reorderQuantity ?? 50;
  const leadTimeDays = product.lead_time_days ?? product.leadTimeDays ?? null;

  if (stock === 0) {
    return {
      level: 'stock-out',
      label: 'Stock-out',
      sub: leadTimeDays ? `${leadTimeDays}d to replenish` : null,
      icon: 'XCircle',
      rowBg: 'bg-red-50/40 dark:bg-red-950/20',
      rowClass: 'border-l-4 border-l-red-500',
      badgeClass: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
    };
  }

  if (stock <= safetyStock) {
    return {
      level: 'critical',
      label: 'Critical',
      sub: leadTimeDays ? `${stock} units · ${leadTimeDays}d lead` : `${stock} units`,
      icon: 'AlertTriangle',
      rowBg: 'bg-red-50/20 dark:bg-red-950/10',
      rowClass: 'border-l-4 border-l-red-400',
      badgeClass: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
    };
  }

  if (stock <= reorderPoint) {
    return {
      level: 'low',
      label: 'Below reorder point',
      sub: `${stock} units`,
      icon: 'AlertTriangle',
      rowBg: 'bg-amber-50/20 dark:bg-amber-950/10',
      rowClass: 'border-l-4 border-l-amber-400',
      badgeClass: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    };
  }

  if (
    product.listing_status === 'suppressed' ||
    product.listing_status === 'inactive_out_of_stock'
  ) {
    return {
      level: 'suppressed',
      label: 'Listing suppressed',
      sub: null,
      icon: 'AlertTriangle',
      rowBg: '',
      rowClass: 'border-l-4 border-l-amber-300',
      badgeClass: 'bg-amber-100 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
    };
  }

  return {
    level: 'nominal',
    label: 'In stock',
    sub: null,
    icon: 'CheckCircle',
    rowBg: '',
    rowClass: '',
    badgeClass: 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400',
  };
}

/**
 * Sort weight by risk urgency. Use with Array.sort():
 *   products.sort((a, b) =>
 *     RISK_SORT_ORDER[computeSkuRisk(a).level] - RISK_SORT_ORDER[computeSkuRisk(b).level]
 *   )
 */
export const RISK_SORT_ORDER = {
  'stock-out': 0,
  'critical': 1,
  'low': 2,
  'suppressed': 3,
  'nominal': 4,
};
